// client/src/utils/http.js
import axios from "axios";
import { getOrganizationIdFromWorkspaceKey } from "./workspaceRequest";
import { recordFailedRequest } from "./errorBuffer";
import { SUPPORT_VIEW_HEADER, isSupportView, isSupportViewApiPath } from "./supportView";

const API_BASE = process.env.REACT_APP_BASE_URL; // e.g. https://api.greenleaves.app
const API_ORIGIN = new URL(API_BASE).origin;

let csrfToken = null;
let csrfInFlight = null;
let refreshInFlight = null;

async function getFreshCsrf() {
  // de-dupe concurrent fetches
  if (!csrfInFlight) {
    csrfInFlight = axios
      .get(`${API_BASE}/csrf-token`, { withCredentials: true })
      .then(({ data }) => {
        csrfToken = data?.csrfToken || null;
        return csrfToken;
      })
      .finally(() => {
        csrfInFlight = null;
      });
  }
  return csrfInFlight;
}

export function resetCsrf() {
  csrfToken = null;
  csrfInFlight = null;
}

function shouldSkipAuthRefresh(url) {
  const path = url.pathname || "";
  return (
    path.endsWith("/user/login") ||
    path.endsWith("/user/login-mfa") ||
    path.endsWith("/user/refresh")
  );
}

async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(
        `${API_BASE}/user/refresh`,
        {},
        {
          withCredentials: true,
          __skipAuthRefresh: true,
        },
      )
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// The workspace selected in THIS tab (set from the store in state/index.js). Every API request
// carries it unless the call names a workspace itself. Before this, each call had to add
// x-organization-id by hand, and the ones that forgot only worked because the server let an
// organization project through without it — the leniency workspace isolation removed.
let workspaceKeyProvider = null;
export function setWorkspaceKeyProvider(provider) {
  workspaceKeyProvider = typeof provider === "function" ? provider : null;
}

const WORKSPACE_HEADER = "x-organization-id";
const hasHeader = (headers, name) => {
  if (!headers) return false;
  if (typeof headers.has === "function") return headers.has(name);
  return Object.keys(headers).some((key) => key.toLowerCase() === name);
};
const setHeader = (config, name, value) => {
  config.headers = config.headers || {};
  if (typeof config.headers.set === "function") config.headers.set(name, value);
  else config.headers[name] = value;
};

// REQUEST interceptor
axios.interceptors.request.use(async (config) => {
  const url = new URL(config.url, API_BASE);
  if (url.origin === API_ORIGIN) {
    config.withCredentials = true;

    if (workspaceKeyProvider && !hasHeader(config.headers, WORKSPACE_HEADER)) {
      const organizationId = getOrganizationIdFromWorkspaceKey(workspaceKeyProvider());
      if (organizationId) setHeader(config, WORKSPACE_HEADER, organizationId);
    }

    if (isSupportView() && isSupportViewApiPath(url.pathname)) {
      setHeader(config, SUPPORT_VIEW_HEADER, "1");
    }

    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      // Always fetch a fresh token before mutating requests
      await getFreshCsrf();
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// RESPONSE interceptor – retry once on CSRF failure
axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const reqUrl = new URL(err?.config?.url || "", API_BASE);

    const isCsrf =
      err?.response?.status === 403 &&
      /csrf/i.test(err?.response?.data?.message || "");
    if (isCsrf && !err.config.__retried) {
      err.config.__retried = true;
      resetCsrf();
      await getFreshCsrf();
      err.config.headers = err.config.headers || {};
      err.config.headers["X-CSRF-Token"] = csrfToken;
      return axios(err.config);
    }

    const is401 = err?.response?.status === 401;
    const isApiOrigin = reqUrl.origin === API_ORIGIN;
    const shouldTryRefresh =
      is401 &&
      isApiOrigin &&
      !err?.config?.__authRetried &&
      !err?.config?.__skipAuthRefresh &&
      !shouldSkipAuthRefresh(reqUrl);

    if (shouldTryRefresh) {
      try {
        await refreshAccessToken();
        err.config.__authRetried = true;
        return axios(err.config);
      } catch {
        err.config.__authFinalFail = true;
      }
    }

    // Help & feedback: remember the failure (path, status, server message — nothing else) so
    // a report sent later carries it. Network errors have no response; status stays null.
    try {
      recordFailedRequest({
        method: err?.config?.method,
        url: err?.config?.url,
        status: err?.response?.status ?? null,
        message: err?.response?.data?.message || err?.message || "",
      });
    } catch {
      /* the buffer must never affect the request's outcome */
    }

    return Promise.reject(err);
  },
);

export default axios;
